import React, { useState, useRef, useEffect, useCallback } from 'react';
import { User, Edit2, Save, X, Camera, Clock, CheckCircle, XCircle, FileText } from 'lucide-react';
import { profileRequestAPI } from '../../services/api';
import { validateImageFile, compressImage } from '../../utils/avatarUtils';
import { RequestPostingModal } from '../../components/forms/RequestPostingModal';

export const UserProfile = ({ user, onUpdateUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [editedUser, setEditedUser] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    organization: user?.organization || '',
    municipality: user?.municipality || '',
    avatar: user?.avatar || null
  });
  const fileInputRef = useRef(null);

  // Posting request state
  const [postingRequest, setPostingRequest] = useState(null);
  const [showPostingModal, setShowPostingModal] = useState(false);

  const fetchPostingRequest = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await profileRequestAPI.getMyRequest(user.id);
      if (res.success) {
        setPostingRequest(res.request);
      }
    } catch (err) {
      // ignore
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPostingRequest();
  }, [fetchPostingRequest]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      // Only allow digits
      const digits = value.replace(/\D/g, '');
      setEditedUser(prev => ({ ...prev, phone: digits }));
      if (digits.length > 0 && digits.length !== 11) {
        setPhoneError('Phone number must be exactly 11 digits.');
      } else {
        setPhoneError('');
      }
    } else {
      setEditedUser(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setErrorMessage(validation.error);
      return;
    }

    try {
      setIsLoading(true);
      const compressedImage = await compressImage(file);
      setEditedUser(prev => ({ ...prev, avatar: compressedImage }));
      setSuccessMessage('Avatar updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to process image. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (editedUser.phone.length > 0 && editedUser.phone.length !== 11) {
      setPhoneError('Phone number must be exactly 11 digits.');
      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const updatedData = { ...user, ...editedUser };
      sessionStorage.setItem('bciis_user', JSON.stringify(updatedData));
      if (onUpdateUser) onUpdateUser(updatedData);

      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const isFarmerOrProcessor = user?.role === 'farmer' || user?.role === 'processor';

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/60 via-orange-50/40 to-amber-100/50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 rounded-3xl p-6 text-white shadow-xl flex flex-col sm:flex-row items-center gap-6">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full border-4 border-white/30 overflow-hidden bg-amber-900 flex items-center justify-center shadow-lg">
              {editedUser.avatar ? (
                <img src={editedUser.avatar} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="w-12 h-12 text-amber-200" />
              )}
            </div>
            {isEditing && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 bg-amber-500 hover:bg-amber-600 text-white p-2 rounded-full shadow border-2 border-white transition"
              >
                <Camera className="w-4 h-4" />
              </button>
            )}
            <input type="file" ref={fileInputRef} onChange={handleAvatarChange} accept="image/*" className="hidden" />
          </div>

          <div className="text-center sm:text-left flex-1">
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
              <h1 className="text-2xl font-extrabold">{user?.fullname || `${editedUser.firstName} ${editedUser.lastName}`.trim() || user?.username}</h1>
              <span className="px-3 py-0.5 rounded-full text-xs font-bold bg-amber-500/30 text-amber-100 border border-amber-400/30 uppercase">
                {user?.role || 'User'}
              </span>
            </div>
            <p className="text-xs text-amber-200 mt-1">@{user?.username} • {editedUser.email}</p>
          </div>

          <div>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-bold text-xs rounded-xl backdrop-blur-md flex items-center gap-1.5 transition"
              >
                <Edit2 className="w-4 h-4" /> Edit Profile
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow flex items-center gap-1 transition"
                >
                  <Save className="w-4 h-4" /> Save
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-bold text-xs rounded-xl backdrop-blur-md flex items-center gap-1 transition"
                >
                  <X className="w-4 h-4" /> Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Notifications */}
        {successMessage && <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-2xl font-semibold">{successMessage}</div>}
        {errorMessage && <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-2xl font-semibold">{errorMessage}</div>}

        {/* Directory Posting Status Box for Farmers & Processors */}
        {isFarmerOrProcessor && (
          <div className="bg-white rounded-3xl p-6 shadow-md border border-amber-100/80">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-700" />
                <h3 className="text-sm font-bold text-gray-900">Public Directory Posting Request Status</h3>
              </div>
              <span className="text-xs text-amber-700 font-semibold capitalize">Role: {user.role}</span>
            </div>

            {postingRequest ? (
              <div className={`p-4 rounded-2xl border text-xs space-y-2 ${
                postingRequest.status === 'approved' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' :
                postingRequest.status === 'rejected' ? 'bg-rose-50 border-rose-200 text-rose-900' :
                'bg-amber-50 border-amber-200 text-amber-900'
              }`}>
                <div className="flex items-center justify-between font-bold">
                  <span className="flex items-center gap-1.5">
                    {postingRequest.status === 'approved' && <CheckCircle className="w-4 h-4 text-emerald-600" />}
                    {postingRequest.status === 'rejected' && <XCircle className="w-4 h-4 text-rose-600" />}
                    {postingRequest.status === 'pending' && <Clock className="w-4 h-4 text-amber-600" />}
                    Posting Request: {postingRequest.status.toUpperCase()}
                  </span>
                  <span className="text-[10px] text-gray-500 font-medium">Submitted {new Date(postingRequest.submittedAt).toLocaleDateString()}</span>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed">
                  {postingRequest.status === 'approved' && 'Your profile information is approved and live in the public directory!'}
                  {postingRequest.status === 'pending' && 'Your posting request is under review by BCIIS Admin.'}
                  {postingRequest.status === 'rejected' && `Request rejected. Reason: ${postingRequest.rejectionReason || 'No reason provided.'}`}
                </p>
                {postingRequest.status === 'rejected' && (
                  <button
                    onClick={() => setShowPostingModal(true)}
                    className="mt-2 px-3 py-1.5 bg-rose-600 text-white rounded-lg font-bold text-[11px]"
                  >
                    Re-submit Request
                  </button>
                )}
                {postingRequest.status === 'approved' && (
                  <button
                    onClick={() => setShowPostingModal(true)}
                    className="mt-2 px-3.5 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-bold text-[11px] shadow transition flex items-center gap-1.5"
                  >
                    Update Directory Profile
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-amber-50/60 p-4 rounded-2xl border border-amber-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                <div>
                  <p className="font-bold text-amber-950">You haven't requested to post your profile yet.</p>
                  <p className="text-amber-800 text-[11px] mt-0.5">Submit a posting request to showcase your {user.role === 'farmer' ? 'farm' : 'business'} details in the directory.</p>
                </div>
                <button
                  onClick={() => setShowPostingModal(true)}
                  className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow whitespace-nowrap transition"
                >
                  Submit Posting Request
                </button>
              </div>
            )}
          </div>
        )}

        {/* Profile Info Card */}
        <div className="bg-white rounded-3xl p-6 shadow-md border border-amber-100/80 space-y-4">
          <h3 className="text-sm font-bold text-gray-900 pb-2 border-b border-gray-100">Personal Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div>
              <label className="block text-gray-500 font-semibold mb-1">First Name</label>
              <input
                type="text"
                name="firstName"
                disabled={!isEditing}
                value={editedUser.firstName}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl bg-gray-50/50 disabled:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-gray-500 font-semibold mb-1">Last Name</label>
              <input
                type="text"
                name="lastName"
                disabled={!isEditing}
                value={editedUser.lastName}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl bg-gray-50/50 disabled:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-gray-500 font-semibold mb-1">Email</label>
              <input
                type="email"
                name="email"
                disabled={!isEditing}
                value={editedUser.email}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl bg-gray-50/50 disabled:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-gray-500 font-semibold mb-1">Phone</label>
              <input
                type="tel"
                inputMode="numeric"
                name="phone"
                maxLength={11}
                disabled={!isEditing}
                value={editedUser.phone}
                onChange={handleInputChange}
                className={`w-full px-3.5 py-2.5 border rounded-xl bg-gray-50/50 disabled:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none ${
                  phoneError ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`}
              />
              {phoneError && isEditing && (
                <p className="mt-1 text-[11px] text-red-500 font-medium">{phoneError}</p>
              )}
              {isEditing && !phoneError && (
                <p className="mt-0.5 text-[10px] text-gray-400">Numbers only · exactly 11 digits (e.g. 09171234567)</p>
              )}
            </div>
          </div>
        </div>

        {/* Modal for Posting Request */}
        {showPostingModal && user && (
          <RequestPostingModal
            user={user}
            role={user.role}
            onClose={() => setShowPostingModal(false)}
            onSuccess={() => {
              setShowPostingModal(false);
              fetchPostingRequest();
            }}
          />
        )}

      </div>
    </div>
  );
};
